import { useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { SCROLL_RESTORE_STATE_KEY } from './useScrollRestore';
import { clearLibraryGridScroll } from '../services/libraryGridScrollSession';
import { loadArtist, loadPerson, loadAlbum } from '../runtime/entityLoaders';
import { isLocalAlbumGetInFlight } from '../services/libraryService';
import { normalizeAlbum } from '../utils/albumUtils';
import { getClientViewHandler } from '../handlers/ClientViewHandler';
import { CLOUD_API_URL } from '../config/serverConfig';
import { useUI } from '../providers/UIProvider';
import { placeholderDataUri } from '../components/library/AlbumGrid';
import { useRecentAlbumsStore, normalizeRecentAlbumId } from '../store/recentAlbumsStore';
import { setLocalNavigationOverride } from './useBatonConnection';
import { logAlbumNavBreadcrumb, logAlbumNavFailure } from '../utils/albumNavigationDiagnostics';
import { logPlaylistNavBreadcrumb } from '../utils/playlistNavigationDiagnostics';
import { performAppHomeNavigation } from '../utils/appHomeNavigation';
import {
  encodeLocalAlbumKey,
  isLocalLibraryAlbumOpen,
  LOCAL_ALBUM_SEARCH_PARAM,
  buildLocalLibraryAlbumUrl,
} from '../utils/localAlbumNavigation';

const __UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const __SERVICE_URI_RE = /^(qobuz|tidal|spotify):(artist|track|album):(.+)$/i;

export function saveRecentAlbum(album) {
  if (!album) return;
  const id = normalizeRecentAlbumId(
    album.album_key || album.serviceAlbumId || album.id,
    album.__source === 'local_library' || album.isLocalLibrary || album.source === 'local_library'
      ? 'local_library'
      : album.source,
  );
  if (!id || !album.title) return;

  const qualityLabel = album.qualityLabel
    || (album.qualityTier?.label && album.maxBitDepth && album.maxSampleRate
      ? `${album.qualityTier.label} · ${album.maxBitDepth}-bit / ${album.maxSampleRate}kHz`
      : null);

  useRecentAlbumsStore.getState().saveRecent({
    album_id: id,
    source: album.source || null,
    title: album.title || '',
    artist: album.artistName || album.artist?.name || album.artist_name || '',
    artwork_url: album.coverUrl || album.cover || album.artworkUrl || album.cover_url || album.image?.large || null,
    embedded_cover_hash: album.embedded_cover_hash ?? album.embeddedCoverHash ?? null,
    folder_cover_hash: album.folder_cover_hash ?? album.folderCoverHash ?? null,
    quality_label: qualityLabel,
  });
}

function isStreamingAlbumDetailPath(pathname) {
  return pathname.startsWith('/app/album/');
}

function isLegacyLocalAlbumPath(pathname) {
  return pathname.startsWith('/app/local-album/');
}

function isAlbumDetailPath(pathname, search) {
  return isStreamingAlbumDetailPath(pathname)
    || isLegacyLocalAlbumPath(pathname)
    || isLocalLibraryAlbumOpen(pathname, search);
}

function resolveReturnTo(location, options) {
  if (options?.returnTo) return options.returnTo;
  const { pathname, search, state } = location;
  if (isAlbumDetailPath(pathname, search)) {
    return state?.returnTo || '/app';
  }
  return `${pathname}${search || ''}`;
}

export default function useAppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewHandler = getClientViewHandler();
  const { setShowSettings, setAlbumOverlay } = useUI();
  const lastLocalOpenRef = useRef({ key: '', at: 0 });

  const closeLocalAlbum = useCallback(() => {
    setLocalNavigationOverride();
    if (isLocalLibraryAlbumOpen(location.pathname, location.search)) {
      navigate(-1);
      return;
    }
    if (isLegacyLocalAlbumPath(location.pathname)) {
      navigate(-1);
    }
  }, [navigate, location.pathname, location.search]);

  const openArtist = useCallback(async (artistIdOrObj, artistNameArg, options = {}) => {
    if (!options?.suppressBroadcast) setLocalNavigationOverride();
    let artistId, artistName, typeHint, sourceHint;
    if (typeof artistIdOrObj === 'object' && artistIdOrObj !== null) {
      artistId = artistIdOrObj.artistId || artistIdOrObj.id || null;
      artistName = artistIdOrObj.artistName || artistIdOrObj.name || '';
      typeHint = artistIdOrObj.type || null;
      sourceHint = artistIdOrObj.__source || null;
    } else {
      artistId = artistIdOrObj;
      artistName = artistNameArg || '';
      typeHint = options?.type || null;
      sourceHint = options?.__source || null;
    }
    let safeName = (artistName || '').replace(/^\s*[-–—]+\s*/, '').trim();
    if (__UUID_RE.test(safeName)) safeName = '';
    const suppressBroadcast = !!options?.suppressBroadcast;

    if (sourceHint === 'local_library') {
      if (!safeName) {
        console.warn('[openAlbum] local_library artist has no name — cannot navigate');
        return;
      }
      console.log('[openArtist] local library artist → library view filtered by', safeName);
      navigate(`/app/local-library?tab=albums&artist=${encodeURIComponent(safeName)}`);
      return;
    }

    const serviceMatch = artistId ? String(artistId).match(__SERVICE_URI_RE) : null;
    if (serviceMatch && safeName) {
      const serviceNumericId = serviceMatch[3];
      options = { ...options, preloadedPayload: { ...(options?.preloadedPayload || {}), serviceArtistId: String(artistId), serviceNumericId } };
      artistId = null;
    }

    if (artistId && __UUID_RE.test(String(artistId)) && !options?._skipResolve) {
      try {
        const res = await fetch(`${CLOUD_API_URL}/api/resolve/${encodeURIComponent(String(artistId))}?_cb=${Date.now()}`, { cache: 'no-store' });
        const resolved = res.ok ? await res.json().catch(() => null) : null;
        if (resolved?.kind === 'person' && resolved?.id && options?.forcePerson) {
          navigate(`/app/person/${encodeURIComponent(resolved.id)}`);
          loadPerson({ personId: String(resolved.id), personName: resolved.name || safeName, triggeredBy: suppressBroadcast ? 'baton' : 'ui', preloadedPayload: options?.preloadedPayload }).catch(() => {});
          if (!suppressBroadcast) viewHandler.navigate('person_detail', { personId: String(resolved.id), personName: resolved.name || safeName });
          return;
        }
        if (resolved?.kind === 'artist' && resolved?.id) {
          artistId = String(resolved.id);
          if (!safeName && resolved?.name) safeName = String(resolved.name).trim();
        }
      } catch {}
    }

    const key = artistId || safeName || 'unknown';
    navigate(`/app/artist/${encodeURIComponent(key)}`);

    loadArtist({
      artistId: artistId || null,
      artistName: safeName || null,
      mbid: options?.mbid || null,
      typeHint,
      triggeredBy: suppressBroadcast ? 'baton' : 'ui',
      hintKind: String(typeHint).toLowerCase() === 'composer' ? 'composer' : undefined,
      preloadedPayload: {
        ...(options?.preloadedPayload || {}),
        ...(String(typeHint).toLowerCase() !== 'composer' ? { profileEntryContext: 'artist' } : {}),
      },
    }).catch(() => {});

    if (!suppressBroadcast) {
      viewHandler.navigate('artist_detail', { artistId, artistName: safeName });
    }
  }, [navigate, viewHandler]);

  const openPerson = useCallback(async (personId, personName, contextRole = null, options = {}) => {
    if (!options?.suppressBroadcast) setLocalNavigationOverride();
    let safeName = (personName || '').replace(/^\s*[-–—]+\s*/, '').trim();
    if (__UUID_RE.test(safeName)) safeName = '';
    const suppressBroadcast = !!options?.suppressBroadcast;
    const key = personId || safeName || 'unknown';
    navigate(`/app/person/${encodeURIComponent(key)}`);

    loadPerson({
      personId: personId || null,
      personName: safeName || null,
      triggeredBy: suppressBroadcast ? 'baton' : 'ui',
      preloadedPayload: { ...(options?.preloadedPayload || {}), role: contextRole || null, name: safeName || personName || null },
    }).catch(() => {});

    if (!suppressBroadcast) {
      viewHandler.navigate('person_detail', { personId, personName: safeName });
    }
  }, [navigate, viewHandler]);

  const openAlbum = useCallback(async (album, options = {}) => {
    if (!album) {
      logAlbumNavFailure('(missing)', 'album object null');
      return;
    }
    if (!options?.suppressBroadcast) setLocalNavigationOverride();

    const _pathLike = (s) => typeof s === 'string' && (s.startsWith('/') || /^[A-Z]:\\/.test(s));
    const isLocal = album?.__source === 'local_library' || album?.isLocalLibrary || !!album?.album_key || album?.source === 'local_library'
      || _pathLike(album?.id) || _pathLike(album?.serviceAlbumId);
    if (isLocal) {
      const raw = album.album_key || album.id || '';
      if (!raw) {
        console.error('[openAlbum] local album has no album_key — cannot open', { album });
        logAlbumNavFailure('(missing)', 'local album missing album_key', { source: 'local_library' });
        return;
      }

      if (isLocalAlbumGetInFlight(raw)) {
        return;
      }

      const now = Date.now();
      if (lastLocalOpenRef.current.key === raw && now - lastLocalOpenRef.current.at < 350) {
        return;
      }
      lastLocalOpenRef.current = { key: raw, at: now };

      const b64 = encodeLocalAlbumKey(raw);
      const searchBefore = window.location.search;
      logAlbumNavBreadcrumb('click', raw, {
        source: 'local_library',
        title: album?.title || null,
        mechanism: 'search_param',
        searchBefore,
      });

      if (location.pathname.startsWith('/app/local-library')) {
        const existingB64 = searchParams.get(LOCAL_ALBUM_SEARCH_PARAM);
        if (existingB64 === b64) return;

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set(LOCAL_ALBUM_SEARCH_PARAM, b64);
          return next;
        }, {
          state: {
            ...location.state,
            album,
            returnTo: location.state?.returnTo ?? resolveReturnTo(location, options),
          },
        });
      } else {
        const next = new URLSearchParams();
        next.set('tab', 'albums');
        next.set(LOCAL_ALBUM_SEARCH_PARAM, b64);
        navigate(`/app/local-library?${next.toString()}`, {
          state: { album, returnTo: resolveReturnTo(location, options) },
        });
      }

      queueMicrotask(() => {
        const searchAfter = window.location.search;
        console.log('[openAlbum] album search param', {
          albumKey: raw,
          b64,
          searchBefore,
          searchAfter,
          title: album?.title || null,
        });
        logAlbumNavBreadcrumb('route', raw, {
          source: 'local_library',
          mechanism: 'search_param',
          b64,
          searchBefore,
          searchAfter,
          navigated: searchAfter.includes(`${LOCAL_ALBUM_SEARCH_PARAM}=${b64}`),
        });
      });
      return;
    }

    const normalized = normalizeAlbum(album, album?.source);
    const albumToUse = normalized || album;
    const suppressBroadcast = !!options?.suppressBroadcast;
    const key = albumToUse?.id;
    const source = albumToUse?.source || album?.source || 'unknown';
    if (!key) {
      console.error('[openAlbum] album has no derivable identity — cannot navigate', { album });
      logAlbumNavFailure('(missing)', 'album has no derivable id', { source, title: albumToUse?.title || album?.title || null });
      return;
    }
    logAlbumNavBreadcrumb('click', String(key), { source, title: albumToUse?.title || null });
    const routePath = `/app/album/${encodeURIComponent(key)}`;
    navigate(routePath, {
      state: {
        source: albumToUse?.source,
        qobuzTracks: options?.keepTracks,
        returnTo: resolveReturnTo(location, options),
      },
    });
    requestAnimationFrame(() => {
      logAlbumNavBreadcrumb('route', String(key), {
        source,
        path: routePath,
        pathname: window.location.pathname,
        navigated: window.location.pathname === routePath,
      });
    });

    if (!suppressBroadcast) {
      viewHandler.navigate('album_detail', { albumId: String(albumToUse.id), albumTitle: albumToUse.title });
    }

    loadAlbum({
      albumId: albumToUse.id,
      prefill: albumToUse,
      triggeredBy: suppressBroadcast ? 'baton' : 'ui',
    }).catch((err) => {
      logAlbumNavFailure(String(key), err?.message || 'loadAlbum rejected', { source });
    });
  }, [navigate, viewHandler, location, searchParams, setSearchParams]);

  const openGenre = useCallback((genreKey, heroImage) => {
    setLocalNavigationOverride();
    navigate(`/app/genre/${encodeURIComponent(genreKey)}`, { state: { heroImage } });
  }, [navigate]);

  const openPlaylist = useCallback((playlist) => {
    if (!playlist?.id) return;
    setLocalNavigationOverride();
    const id = String(playlist.id);
    const source = playlist.source || 'qobuz';
    logPlaylistNavBreadcrumb('click', id, { source, title: playlist.title || playlist.name || null });
    navigate(`/app/playlist/${encodeURIComponent(id)}`, { state: { playlist } });
    logPlaylistNavBreadcrumb('route', id, { source, title: playlist.title || playlist.name || null });
    viewHandler.navigate('playlist_detail', { playlistId: id, playlistTitle: playlist.title || playlist.name });
  }, [navigate, viewHandler]);

  const goHome = useCallback(() => {
    performAppHomeNavigation(navigate, viewHandler);
  }, [navigate, viewHandler]);

  const goGenres = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/genres');
  }, [navigate]);

  const goHistory = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/history');
  }, [navigate]);

  const goBrowse = useCallback((tab) => {
    setLocalNavigationOverride();
    const path = tab ? `/app/browse?tab=${tab}` : '/app/browse';
    navigate(path);
    viewHandler.navigate('grid', { tabId: tab });
  }, [navigate, viewHandler]);

  const goRadio = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/radio');
    viewHandler.navigate('live_radio', {});
  }, [navigate, viewHandler]);

  const goEllaRadio = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/ella-radio');
    viewHandler.navigate('ella_radio', {});
  }, [navigate, viewHandler]);

  const goMaestroRadio = goEllaRadio;

  const goOctaveRadio = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/octave-radio');
    viewHandler.navigate('octave_radio', {});
  }, [navigate, viewHandler]);

  const goQobuz = useCallback((subView) => {
    setLocalNavigationOverride();
    navigate(subView === 'search' ? '/app/qobuz/search' : '/app/qobuz');
    viewHandler.navigate('qobuz_browse', {});
  }, [navigate, viewHandler]);

  const goFavorites = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/favorites');
  }, [navigate]);

  const goAudiophilePlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/audiophile-playlists');
  }, [navigate]);

  const goGenrePlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/genre-playlists');
  }, [navigate]);

  const goMoodPlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/mood-playlists');
  }, [navigate]);

  const goSeasonPlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/season-playlists');
  }, [navigate]);

  const goMyPlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/my-playlists');
  }, [navigate]);

  const goSmartPlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/smart-playlists');
  }, [navigate]);

  const goFavoritePlaylists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/favorite-playlists');
  }, [navigate]);

  const goAudiophilePlaylist = useCallback((pid) => {
    setLocalNavigationOverride();
    navigate(`/app/playlist/${encodeURIComponent(pid)}`, { state: { fromAudiophile: true } });
  }, [navigate]);

  const goFavoritePlaylist = useCallback((pid) => {
    setLocalNavigationOverride();
    navigate(`/app/playlist/${encodeURIComponent(pid)}`, { state: { fromFavorites: true } });
  }, [navigate]);

  const goValet = useCallback((subView = 'for-you') => {
    setLocalNavigationOverride();
    navigate(`/app/valet/${subView}`);
  }, [navigate]);

  const goSupport = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/support');
  }, [navigate]);

  const goSettings = useCallback(() => {
    setLocalNavigationOverride();
    setShowSettings(true);
  }, [setShowSettings]);

  const goLibrary = useCallback((tab) => {
    setLocalNavigationOverride();
    navigate(tab ? `/app/library?tab=${tab}` : '/app/library');
  }, [navigate]);

  const goLocalLibrary = useCallback((tab) => {
    setLocalNavigationOverride();
    clearLibraryGridScroll();
    navigate(tab ? `/app/local-library?tab=${tab}` : '/app/local-library');
  }, [navigate]);

  const goDevices = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/devices');
  }, [navigate]);

  const goVerifiedDevices = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/devices');
  }, [navigate]);

  const goPrism = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/prism');
  }, [navigate]);

  const goStreamAlbums = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/stream-albums');
  }, [navigate]);

  const goStreamArtists = useCallback(() => {
    setLocalNavigationOverride();
    navigate('/app/stream-artists');
  }, [navigate]);

  const goLocalAlbum = useCallback((albumKey) => {
    setLocalNavigationOverride();
    const b64 = encodeLocalAlbumKey(albumKey);
    navigate(buildLocalLibraryAlbumUrl(b64));
  }, [navigate]);

  const goBack = useCallback(() => {
    setLocalNavigationOverride();
    const returnTo = location.state?.returnTo;

    if (isLocalLibraryAlbumOpen(location.pathname, location.search)) {
      navigate(-1);
      return;
    }

    if (isLegacyLocalAlbumPath(location.pathname) || isStreamingAlbumDetailPath(location.pathname)) {
      navigate(-1);
      return;
    }

    if (typeof returnTo === 'string' && returnTo.length > 0) {
      navigate(returnTo, {
        replace: true,
        state: { [SCROLL_RESTORE_STATE_KEY]: true },
      });
      return;
    }
    navigate(-1);
  }, [navigate, location.pathname, location.search, location.state]);

  return {
    openArtist,
    openPerson,
    openAlbum,
    openGenre,
    openPlaylist,
    goHome,
    goGenres,
    goHistory,
    goBrowse,
    goRadio,
    goEllaRadio,
    goMaestroRadio,
    goOctaveRadio,
    goQobuz,
    goFavorites,
    goAudiophilePlaylists,
    goGenrePlaylists,
    goMoodPlaylists,
    goSeasonPlaylists,
    goMyPlaylists,
    goSmartPlaylists,
    goFavoritePlaylists,
    goAudiophilePlaylist,
    goFavoritePlaylist,
    goValet,
    goSupport,
    goSettings,
    goDevices,
    goVerifiedDevices,
    goPrism,
    goLibrary,
    goLocalLibrary,
    goStreamAlbums,
    goStreamArtists,
    goLocalAlbum,
    closeLocalAlbum,
    goBack,
    navigate,
  };
}
